import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { queryKeyRootForEntity } from '@/hooks/queryKeyRegistry.generated';
import { moduleNavigation } from '@/config/navigation.generated';

export interface PageEntityContext {
  module?: string;
  entityType?: string;
  entityId?: string;
  /** Field carrying the record's business identity, e.g. 'orderNumber'. */
  displayField?: string;
}

interface EntityRoute {
  path: string;
  entity: string;
  module: string;
  displayField?: string;
}

/**
 * Entity routes derived from the generated navigation config — the same
 * source the menu renders from, so every entity page resolves without a
 * hand-maintained route map (which is what this replaced: a hardcoded map
 * covering 11 of ~90 entities silently returned no context elsewhere).
 */
let cachedRoutes: EntityRoute[] | null = null;

function entityRoutes(): EntityRoute[] {
  if (!cachedRoutes) {
    const routes: EntityRoute[] = [];
    for (const mod of moduleNavigation) {
      for (const category of mod.categories) {
        for (const item of category.items) {
          if (item.entity) {
            routes.push({
              path: item.path,
              entity: item.entity,
              module: mod.module,
              displayField: item.displayField,
            });
          }
        }
      }
    }
    // Longest path first so nested paths match their most specific entity.
    routes.sort((a, b) => b.path.length - a.path.length);
    cachedRoutes = routes;
  }
  return cachedRoutes;
}

/**
 * Canonical (PascalCase) module name for permission strings — lookup configs
 * carry lowercase module names ("sales"), while permissions are granted as
 * "Sales.Customer.Read". Resolved from the generated navigation config so
 * multi-word modules (TaskManagement) come out right; unknown modules fall
 * back to capitalizing the first letter.
 */
export function canonicalModuleName(module: string): string {
  const match = moduleNavigation.find((m) => m.module.toLowerCase() === module.toLowerCase());
  return match?.module ?? module.charAt(0).toUpperCase() + module.slice(1);
}

/**
 * Detail-view path for a referenced entity, resolved from the generated
 * navigation config — the same source the menu and page context read — so
 * irregular plurals come out right (Country → /core/countries). Entities
 * hidden from navigation are absent there and fall back to the
 * /{module}/{entity}s convention the generated routes follow.
 */
/**
 * Detail path for a record when only its entity NAME is known — a push notification naming
 * what it changed, a deep link. Resolved from the generated navigation, so it covers every
 * entity with a page instead of a hand-maintained map. An entity that also has an alias or
 * preset entry (OrgUnit is under both /core/organization and /core/orgunits) resolves to the
 * canonical /{module}/{plural} one, matched against the generated query-key root. Undefined
 * when the entity has no page of its own — the caller leaves it non-navigable.
 */
export function entityDetailPathByName(entity: string, id: string): string | undefined {
  const candidates = entityRoutes().filter(
    (route) => route.entity.toLowerCase() === entity.toLowerCase(),
  );
  if (candidates.length === 0) return undefined;

  const plural = queryKeyRootForEntity(entity);
  const canonical =
    candidates.find((route) => lastPathSegment(route.path) === plural) ?? candidates[0];
  return `${stripQuery(canonical.path)}/${id}`;
}

const stripQuery = (path: string): string => path.split('?')[0];
const lastPathSegment = (path: string): string => stripQuery(path).split('/').filter(Boolean).pop() ?? '';

export function entityDetailPath(module: string, entity: string, id: string): string {
  return `${entityListPath(module, entity)}/${id}`;
}

/**
 * List-view path for an entity, resolved the same way as {@link entityDetailPath}. With
 * `filter`, the list opens pre-filtered on that field (the URL contract EntityListView reads:
 * `filter.<field>`, optional `filterLabel.<field>` for the chip text) — the way a detail view
 * hands off to "all related records in the full grid".
 */
export function entityListPath(
  module: string,
  entity: string,
  filter?: { field: string; value: string; label?: string },
): string {
  const match = entityRoutes().find(
    (route) =>
      route.entity.toLowerCase() === entity.toLowerCase() &&
      route.module.toLowerCase() === module.toLowerCase(),
  );
  const basePath = match?.path ?? `/${module.toLowerCase()}/${entity.toLowerCase()}s`;
  if (!filter) return basePath;
  const params = new URLSearchParams();
  params.set(`filter.${filter.field}`, filter.value);
  if (filter.label) params.set(`filterLabel.${filter.field}`, filter.label);
  return `${basePath}?${params.toString()}`;
}

/**
 * Resolve the entity context for a pathname. Exported for tests; the hook
 * below is the React-facing wrapper.
 */
export function resolvePageEntityContext(pathname: string): PageEntityContext {
  const match = entityRoutes().find(
    (route) => pathname === route.path || pathname.startsWith(route.path + '/'),
  );
  if (!match) {
    // Non-entity pages (dashboards, module landing pages): module context
    // only, matched against the generated module names.
    const firstSegment = pathname.split('/').filter(Boolean)[0]?.toLowerCase();
    const mod = firstSegment
      ? moduleNavigation.find((m) => m.module.toLowerCase() === firstSegment)
      : undefined;
    return mod ? { module: mod.module } : {};
  }

  // '/sales/orders/abc-123' → id; '/sales/orders/template/new' and other
  // sub-routes are not record ids.
  const remainder = pathname.slice(match.path.length).split('/').filter(Boolean);
  const entityId =
    remainder.length === 1 && remainder[0] !== 'new' && remainder[0] !== 'template'
      ? remainder[0]
      : undefined;

  return {
    module: match.module,
    entityType: match.entity,
    entityId,
    displayField: match.displayField,
  };
}

/**
 * Derives the current entity context from the route for the AI assistant.
 * When on `/sales/orders/abc-123`, returns { module: 'Sales', entityType: 'Order', entityId: 'abc-123' }.
 */
export function usePageEntityContext(): PageEntityContext {
  const location = useLocation();
  return useMemo(() => resolvePageEntityContext(location.pathname), [location.pathname]);
}
